import _ from 'lodash';
import type { Editor, Range, SuggestionListRef } from 'OpalChat/TiptapEditorTypes';
import { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from 'react';
import { Flex, Separator } from '@optiaxiom/react';
import {
  IconBracketsCurly,
  IconCalendar,
  IconFile,
  IconInputNumeric,
  IconInputText,
  IconPen,
  IconPlus,
  IconScrewdriverWrench,
  IconToggleOff,
} from '@optimizely/axiom-icons';

import type { AgentParameter, AgentParameterType } from '@frontend/types';

import type { SuggestionItem } from '../types';

import { styles } from './AgentFormPromptTemplate.css';
import { SectionHeader, SuggestionItem as SuggestionItemComponent } from './Common';

const iconMap: Record<string, React.ReactNode> = {
  string: <IconInputText />,
  number: <IconInputNumeric />,
  boolean: <IconToggleOff />,
  datetime: <IconCalendar />,
  json: <IconBracketsCurly />,
  object: <IconBracketsCurly />,
  file: <IconFile />,
};

export type AgentFormPromptTemplateProps = {
  editor: Editor;
  range: Range;
  command: (item: SuggestionItem) => void;
  variables: Array<AgentParameter<AgentParameterType>> | null;
  onCreateVariable: () => void;
  onEditVariable: (variable: AgentParameter<AgentParameterType>) => void;
  onInsertVariable?: (editor: Editor, variableName: string) => void;
  tools?: string[];
  onAddTool: () => void;
  onEditTool: (tool: string) => void;
  onInsertTool: (editor: Editor, toolName: string) => void;
  onInsertInstructions: (editor: Editor) => void;
  onInsertRetrieval: (editor: Editor) => void;
  onInsertRetrievalWithFiles: (editor: Editor) => void;
};

export const AgentFormPromptTemplate = forwardRef<SuggestionListRef, AgentFormPromptTemplateProps>(
  (
    {
      editor,
      range,
      command,
      variables,
      onCreateVariable,
      onEditVariable,
      onInsertVariable,
      tools,
      onAddTool,
      onEditTool,
      onInsertTool,
      onInsertInstructions,
      onInsertRetrieval,
      onInsertRetrievalWithFiles,
    },
    ref
  ) => {
    const [selectedIndex, setSelectedIndex] = useState(0);

    const suggestionItems: SuggestionItem[] = useMemo(() => {
      const items: SuggestionItem[] = [];
      // variables
      variables?.forEach(variable => {
        items.push({
          id: `variable:${variable.name}`,
          label: variable.name,
          type: 'variable',
          data: variable,
        });
      });
      items.push({
        id: 'action:create-variable',
        label: 'Create variable',
        type: 'action',
        actionType: 'create-variable',
      });
      // tools
      tools?.forEach(tool => {
        items.push({
          id: `tool:${tool}`,
          label: tool,
          type: 'tool',
          data: tool,
        });
      });
      items.push({
        id: 'action:add-tool',
        label: 'Add tool',
        type: 'action',
        actionType: 'add-tool',
      });

      // Remaining Actions
      items.push({
        id: 'action:add-instructions',
        label: 'Add instructions',
        type: 'action',
        actionType: 'add-instructions',
      });
      items.push({
        id: 'action:add-retrieval',
        label: 'Add retrieval query',
        type: 'action',
        actionType: 'add-retrieval',
      });
      items.push({
        id: 'action:add-retrieval-files',
        label: 'Add retrieval query with files',
        type: 'action',
        actionType: 'add-retrieval-files',
      });
      return items;
    }, [variables, tools]);

    const handleSelect = useCallback(
      (item: SuggestionItem) => {
        // Delete the slash command range first (removes "/" and any query text)
        editor.chain().focus().deleteRange(range).run();

        if (item.type === 'variable' && item.data && onInsertVariable) {
          const variable = item.data as AgentParameter<AgentParameterType>;
          onInsertVariable(editor, variable.name);
          command(item);
          return;
        }
        if (item.type === 'tool' && item.data && onInsertTool) {
          const tool = item.data as string;
          onInsertTool(editor, tool);
          command(item);
          return;
        }

        if (item.type === 'action') {
          switch (item.actionType) {
            case 'create-variable':
              onCreateVariable();
              command(item);
              break;
            case 'add-tool':
              onAddTool();
              command(item);
              break;
            case 'add-instructions':
              onInsertInstructions(editor);
              command(item);
              break;
            case 'add-retrieval':
              onInsertRetrieval(editor);
              command(item);
              break;
            case 'add-retrieval-files':
              onInsertRetrievalWithFiles(editor);
              command(item);
              break;
            default:
              break;
          }
        }
      },
      [
        editor,
        range,
        command,
        onInsertVariable,
        onInsertTool,
        onCreateVariable,
        onAddTool,
        onInsertInstructions,
        onInsertRetrieval,
        onInsertRetrievalWithFiles,
      ]
    );

    useImperativeHandle(
      ref,
      () => ({
        onKeyDown: ({ event }: { event: KeyboardEvent }) => {
          if (event.key === 'ArrowUp') {
            setSelectedIndex(prev => (prev + suggestionItems.length - 1) % suggestionItems.length);
            return true;
          }
          if (event.key === 'ArrowDown') {
            setSelectedIndex(prev => (prev + 1) % suggestionItems.length);
            return true;
          }
          if (event.key === 'Enter') {
            const item = suggestionItems[selectedIndex];
            if (item) {
              handleSelect(item);
            }
            return true;
          }
          if (event.key === 'Tab') {
            if (event.shiftKey) {
              setSelectedIndex(
                prev => (prev + suggestionItems.length - 1) % suggestionItems.length
              );
            } else {
              const item = suggestionItems[selectedIndex];
              if (item) {
                handleSelect(item);
              }
            }
            return true;
          }
          return false;
        },
      }),
      [suggestionItems, selectedIndex, handleSelect]
    );

    const selectedId = suggestionItems[selectedIndex]?.id;

    const itemsById = useMemo(() => _.keyBy(suggestionItems, 'id'), [suggestionItems]);

    const handleItemClick = useCallback(
      (id: string) => {
        const item = itemsById[id];
        if (item) handleSelect(item);
      },
      [itemsById, handleSelect]
    );

    return (
      <Flex
        flexDirection="column"
        gap="6"
        rounded="md"
        shadow="md"
        border="1"
        borderColor="border.default"
        overflow="auto"
        bg="bg.default"
        pt="6"
        pb="12"
        className={styles.container}
      >
        {/* Variables Section */}
        <SectionHeader title="Variables" />
        {variables?.map(variable => {
          const itemId = `variable:${variable.name}`;
          return (
            <SuggestionItemComponent
              key={variable.name}
              iconBefore={iconMap[variable.type] || <IconInputText />}
              label={variable.name}
              iconAfter={<IconPen />}
              isSelected={selectedId === itemId}
              onClick={() => handleItemClick(itemId)}
              onEditClick={() => onEditVariable?.(variable)}
            />
          );
        })}
        <SuggestionItemComponent
          iconBefore={<IconPlus />}
          label="Create variable"
          isAction
          isSelected={selectedId === 'action:create-variable'}
          onClick={() => handleItemClick('action:create-variable')}
        />

        {/* Tools Section */}
        <Separator />
        <SectionHeader title="Tools" />
        {tools?.map(tool => {
          const itemId = `tool:${tool}`;
          return (
            <SuggestionItemComponent
              key={tool}
              iconBefore={<IconScrewdriverWrench />}
              label={tool}
              iconAfter={<IconPen />}
              isSelected={selectedId === itemId}
              onClick={() => handleItemClick(itemId)}
              onEditClick={() => onEditTool?.(tool)}
            />
          );
        })}
        <SuggestionItemComponent
          iconBefore={<IconPlus />}
          label="Add tool"
          isAction
          isSelected={selectedId === 'action:add-tool'}
          onClick={() => handleItemClick('action:add-tool')}
        />

        {/* Instructions Section */}
        <Separator />
        <SectionHeader title="Instructions" />
        <SuggestionItemComponent
          iconBefore={<IconPlus />}
          label="Add instructions"
          isAction
          isSelected={selectedId === 'action:add-instructions'}
          onClick={() => handleItemClick('action:add-instructions')}
        />

        {/* Retrieval Section */}
        <Separator />
        <SectionHeader title="Retrieval (RAG)" />
        <SuggestionItemComponent
          iconBefore={<IconBracketsCurly />}
          label="Retrieve text"
          isAction
          isSelected={selectedId === 'action:add-retrieval'}
          onClick={() => handleItemClick('action:add-retrieval')}
        />
        <SuggestionItemComponent
          iconBefore={<IconBracketsCurly />}
          label="Retrieve files"
          isAction
          isSelected={selectedId === 'action:add-retrieval-files'}
          onClick={() => handleItemClick('action:add-retrieval-files')}
        />
      </Flex>
    );
  }
);

AgentFormPromptTemplate.displayName = 'AgentFormPromptTemplate';
